import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Fuel Injectors", to: "/shop/Fuel%20Injector%20Sets" },
  { label: "Find Your Part", to: "/search/part" },
  { label: "Shop by Vehicle", to: "/vehicle-finder" },
  { label: "Wholesale", to: "/wholesale" },
  { label: "About", to: "/about" },
  { label: "Support", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q } });
        setSuggestions(data.items || []);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearchOpen(false);
    navigate(`/search/part?pn=${encodeURIComponent(q.trim())}`);
    setQ("");
  };

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${solid ? "bg-[#0B0F19]/95 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center gap-6 h-16 lg:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-md bg-[#0055FF] flex items-center justify-center relative">
            <div className="absolute inset-0 rounded-md bg-gradient-to-br from-white/30 to-transparent" />
            <span className="font-display text-white text-lg leading-none relative">IC</span>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-white text-lg tracking-tight">InterCorp</span>
            <span className="text-[9px] font-mono text-slate-400 tracking-[0.3em] uppercase">Industries</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-md transition-colors"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="header-search-toggle"
                aria-label="Search"
                className="w-10 h-10 rounded-md text-slate-200 hover:bg-white/10 flex items-center justify-center"
              >
                <Search className="w-4 h-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="top" className="bg-[#0B0F19] border-white/10 text-slate-100">
              <div className="max-w-3xl mx-auto py-8">
                <form onSubmit={submitSearch} className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    data-testid="global-search-input"
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Enter an OEM, manufacturer, interchange or SKU part number…"
                    className="pl-12 h-14 bg-white/5 border-white/10 text-lg text-slate-100 font-mono placeholder:text-slate-500"
                  />
                </form>
                {suggestions.length > 0 && (
                  <div className="mt-4 border border-white/10 rounded-lg overflow-hidden bg-[#151A26] divide-y divide-white/5">
                    {suggestions.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/product/${s.slug}`}
                        onClick={() => setSearchOpen(false)}
                        data-testid={`suggestion-${s.slug}`}
                        className="flex items-center gap-4 p-3 hover:bg-white/5 transition-colors"
                      >
                        <div className="w-14 h-14 bg-white rounded-md overflow-hidden shrink-0">
                          {s.main_image && <img src={s.main_image} alt="" className="w-full h-full object-contain" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 truncate">{s.title}</div>
                          <div className="text-xs text-slate-400 font-mono">{s.sku}</div>
                        </div>
                        <div className="text-sm text-[#0055FF] font-semibold">${s.price?.toFixed(2)}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <Link
            to={user ? "/account" : "/account"}
            data-testid="header-account"
            className="w-10 h-10 rounded-md text-slate-200 hover:bg-white/10 flex items-center justify-center"
            aria-label="Account"
          >
            <User className="w-4 h-4" />
          </Link>

          <button
            data-testid="header-cart-button"
            onClick={() => setCartOpen(true)}
            aria-label="Cart"
            className="relative w-10 h-10 rounded-md text-slate-200 hover:bg-white/10 flex items-center justify-center"
          >
            <ShoppingCart className="w-4 h-4" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#0055FF] text-white text-[10px] font-semibold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden w-10 h-10 rounded-md text-slate-200 hover:bg-white/10 flex items-center justify-center">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B0F19] border-white/10 text-slate-100 w-[80%] sm:w-[380px]">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} className="py-3 px-2 border-b border-white/5 text-slate-100">
                    {n.label}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
